/* eslint-disable @wyz/module/no-relative-import */
import { DateUtils, invariant } from '@wyz/utils';
import { get, isNil, set, uniq } from 'lodash-es';
import {
  IBrand,
  ICriteriaOptionItem,
  ISeasonOptionItem,
  ISupplierTypeOptionItem,
  IVehicleTypeGroupList,
  IVehicleTypeGroupsOptionItem,
  TranslationKeys,
} from '~/common/types';
import {
  ICreateRedistributionPricingPolicyPayload,
  IDiameter,
  IGetOneRedistributionPricingPolicy,
  IGlobalRedistributionPricingFormData,
  IPolicyDefinitionValue,
  IPricingStatus,
  IRedistributionPolicyItem,
  IRedistributionPricingConfigurationStepForm,
  IRedistributionPricingSectionPayload,
  RedistributionPricingPolicyFormStep,
} from '~/purchase/types';

import { DEFAULT_VEHICLE_TYPE } from '~/purchase/helpers';
import {
  CUSTOMER_CRITERIA_CODE,
  NO_REFINEMENT_VALUE,
  buildVehicleTypeLookup,
  getDiameterPayload,
  getLookupValue,
} from './common';

// Dev's note : The order is very important, we use this array as data source for the UI Stepper
export const REDISTRIBUTION_PRICING_POLICY_STEP_LABELS: Record<
  RedistributionPricingPolicyFormStep,
  keyof TranslationKeys
> = {
  APPLICATION:
    'app.pricing_policy.redistribution.form.application_step.wizard_title',
  PRICING: 'app.pricing_policy.redistribution.form.pricing_step.wizard_title',
};
export const REDISTRIBUTION_PRICING_POLICY_STEP_INDEX: Record<
  RedistributionPricingPolicyFormStep,
  number
> = {
  APPLICATION: 0,
  PRICING: 1,
} as const;
export const FIRST_STEP = Math.min(
  ...Object.values(REDISTRIBUTION_PRICING_POLICY_STEP_INDEX),
);
export const LAST_STEP = Math.max(
  ...Object.values(REDISTRIBUTION_PRICING_POLICY_STEP_INDEX),
);

export const hasRefined = (item: IRedistributionPolicyItem) => {
  return (
    item.refinementFlags.diameter ||
    item.refinementFlags.season ||
    item.refinementFlags.supplierType ||
    item.refinementFlags.tyreType
  );
};

const buildLookups = (
  seasons: Array<ISeasonOptionItem>,
  supplierTypes: Array<ISupplierTypeOptionItem>,
  brands: Array<IBrand>,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
) => {
  const brandsLookup = brands.reduce(
    (acc, curr) => {
      acc[String(curr.id)] = { key: String(curr.id), name: curr.name };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );
  const seasonsLookup = seasons.reduce(
    (acc, curr) => {
      acc[curr.label] = { key: String(curr.value), name: curr.label };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );
  const supplierTypesLookup = supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.value] = { key: String(curr.id), name: curr.label };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );

  return {
    brandsLookup,
    seasonsLookup,
    supplierTypesLookup,
    ...buildVehicleTypeLookup(vehicleTypeGroups),
  };
};

export const formToPayload = (
  formData: IGlobalRedistributionPricingFormData,
  status: IPricingStatus,
  seasons: Array<ISeasonOptionItem>,
  supplierTypes: Array<ISupplierTypeOptionItem>,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
  criteria: Array<ICriteriaOptionItem>,
  brands: Array<IBrand>,
): ICreateRedistributionPricingPolicyPayload => {
  invariant(formData.PRICING);
  invariant(formData.APPLICATION);
  const {
    brandsLookup,
    seasonsLookup,
    supplierTypesLookup,
    vehicleTypeGroupLookup,
    vehicleTypeLookup,
  } = buildLookups(seasons, supplierTypes, brands, vehicleTypeGroups);
  return {
    name: formData.PRICING.name,
    vehicleTypeGroup: getLookupValue(
      formData.PRICING.vehicleTypeGroup,
      vehicleTypeGroupLookup,
    ),
    startDate: DateUtils.formatTimestamp(formData.APPLICATION?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData.APPLICATION?.endDate
      ? DateUtils.formatTimestamp(formData.APPLICATION?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    pricingPolicyType: 'redistribution',
    criteria: [
      // seller criteria
      ...formData.APPLICATION.supplierIds.map((supplierId) => ({
        criterion: '',
        id: '',
        code: CUSTOMER_CRITERIA_CODE,
        value: supplierId,
      })),

      // buyer criteria
      ...formData.APPLICATION.customersCriteria.flatMap((item) =>
        item.values.map((value) => {
          const criterion = criteria.find(
            (criteria) => criteria.id === item.criterion.id,
          );
          return {
            criterion: criterion?.name ?? '',
            id: item.criterion.id,
            code:
              criterion?.values.find(
                (criteriaValue) => criteriaValue.id === value,
              )?.label ?? '',

            value,
          };
        }),
      ),
    ],
    sections: flattenRefinements(
      formData.PRICING.configurations,
      brandsLookup,
      seasonsLookup,
      supplierTypesLookup,
      vehicleTypeLookup,
    ),
  };
};

export const flattenRefinements = (
  configurations: IRedistributionPricingConfigurationStepForm['configurations'],
  brandsLookup: Record<string, IPolicyDefinitionValue>,
  seasonsLookup: Record<string, IPolicyDefinitionValue>,
  supplierTypesLookup: Record<string, IPolicyDefinitionValue>,
  vehicleTypeLookup: Record<string, IPolicyDefinitionValue>,
): IRedistributionPricingSectionPayload[] => {
  const sections: IRedistributionPricingSectionPayload[] = [];
  configurations.forEach((configuration, configurationIndex) => {
    const supplierTypes: Array<IPolicyDefinitionValue> =
      configuration.supplierTypes.map((codename) =>
        getLookupValue(codename, supplierTypesLookup),
      );
    sections.push({
      configurationType: configuration.configurationType,
      brands: configuration.brands.map((brandId) =>
        getLookupValue(String(brandId), brandsLookup),
      ),
      name: configuration.name,
      policies: [],
      supplierTypes: supplierTypes.map((item) => Number(item.key)),
    });
    if (!hasRefined(configuration)) {
      invariant(!isNil(configuration.configurationTypeValue));
      sections[configurationIndex]['policies'].push({
        diameter: null,
        priceValue: {
          c: configuration.configurationTypeValue,
          cg1: 0,
          cg2: 0,
          cg3: 0,
          cg4: 0,
          cg5: 0,
        },
        season: null,
        tyreType: null,
        supplierType: null,
      });
    } else {
      Object.keys(configuration.definition?.supplierTypes ?? {}).forEach(
        (supplierTypeCodename) => {
          const refinements =
            configuration.definition?.supplierTypes[supplierTypeCodename]
              .refinements || [];
          refinements.forEach((refinement) => {
            Object.entries(refinement?.seasons ?? {}).forEach(
              ([seasonLabel, tyreTypes]) => {
                Object.entries(tyreTypes.tyreTypes).forEach(
                  ([tyreTypeKey, value]) => {
                    const diameter = getDiameterPayload(refinement?.diameter);

                    const tyreType =
                      tyreTypeKey === NO_REFINEMENT_VALUE
                        ? null
                        : getLookupValue(tyreTypeKey, vehicleTypeLookup);
                    const supplierType =
                      supplierTypeCodename === NO_REFINEMENT_VALUE
                        ? null
                        : getLookupValue(
                            supplierTypeCodename,
                            supplierTypesLookup,
                            false,
                          );
                    // null is a valid value, undefined is not
                    if (tyreType !== undefined && supplierType !== undefined) {
                      sections[configurationIndex]['policies'].push({
                        diameter: diameter,
                        priceValue: {
                          c: value.value,
                          cg1: 0,
                          cg2: 0,
                          cg3: 0,
                          cg4: 0,
                          cg5: 0,
                        },
                        season:
                          seasonLabel === NO_REFINEMENT_VALUE
                            ? null
                            : getLookupValue(seasonLabel, seasonsLookup),
                        tyreType,
                        supplierType,
                      });
                    }
                  },
                );
              },
            );
          });
        },
      );
    }
  });

  return sections;
};

type InitialValuesOptions = {
  vehicleTypeGroups: Array<IVehicleTypeGroupsOptionItem>;
};

export const getInitialValues = ({
  vehicleTypeGroups,
}: InitialValuesOptions): IGlobalRedistributionPricingFormData => {
  const REDISTRIBUTION_PRICING_POLICY_DEFAULT_VALUES: IGlobalRedistributionPricingFormData =
    {
      PRICING: {
        configurations: [],
        name: '',
        vehicleTypeGroup:
          vehicleTypeGroups.find((item) => item.name === DEFAULT_VEHICLE_TYPE)
            ?.value ?? '',
      },
      APPLICATION: {
        supplierIds: [],
        customersCriteria: [],
        startDate: undefined as unknown as Date,
      },
    };
  return {
    ...REDISTRIBUTION_PRICING_POLICY_DEFAULT_VALUES,
  };
};

type InitialValuesFromPayloadOptions = {
  supplierTypes: Array<ISupplierTypeOptionItem>;
  availableBrandIds: Array<number>;
};

export const getInitialValuesFromPayload = (
  payload: IGetOneRedistributionPricingPolicy,
  { supplierTypes, availableBrandIds }: InitialValuesFromPayloadOptions,
): IGlobalRedistributionPricingFormData => {
  const supplierTypesLookup = supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as Record<string, ISupplierTypeOptionItem>,
  );
  const REDISTRIBUTION_PRICING_POLICY_DEFAULT_VALUES: IGlobalRedistributionPricingFormData =
    {
      PRICING: {
        name: payload.name,
        vehicleTypeGroup: payload.vehicleTypeGroup.key,
        configurations: payload.sections.map((section) => {
          const sectionSupplierTypes = section.supplierTypes.map(
            (supplierTypeId) => supplierTypesLookup[supplierTypeId].value,
          );
          const refinementDefaults: ReturnType<typeof getRefinementDefaults> =
            section.policies.length === 1
              ? {
                  definition: null,
                  refinementFlags: {
                    diameter: false,
                    season: false,
                    supplierType: false,
                    tyreType: false,
                  },
                }
              : getRefinementDefaults(
                  section,
                  section.supplierTypes,
                  supplierTypesLookup,
                );
          return {
            name: section.name,
            brands: section.brands
              .filter((brand) => availableBrandIds.includes(Number(brand.key)))
              .map((brand) => Number(brand.key)),
            configurationTypeValue:
              section.policies.length === 1
                ? section.policies[0].priceValue.c
                : 0,
            configurationType: section.configurationType,
            supplierTypes: sectionSupplierTypes,
            refinementFlags: refinementDefaults.refinementFlags,
            definition: refinementDefaults.definition,
          };
        }),
      },
      APPLICATION: {
        startDate: new Date(payload.formattedStartDate),
        endDate: payload.formattedEndDate
          ? new Date(payload.formattedEndDate)
          : null,
        supplierIds: [
          ...payload.criteria
            .filter((criteria) => criteria.code === CUSTOMER_CRITERIA_CODE)
            .map((criteria) => criteria.value),
        ],
        customersCriteria: Object.values(
          payload.criteria
            .filter((criteria) => criteria.code !== CUSTOMER_CRITERIA_CODE)
            .reduce(
              (acc, criteria) => {
                if (!acc[criteria.id]) {
                  acc[criteria.id] = {
                    criterion: { id: criteria.id },
                    values: [],
                  };
                }
                acc[criteria.id].values.push(criteria.value);
                return acc;
              },
              {} as Record<
                string,
                { criterion: { id: string }; values: string[] }
              >,
            ),
        ),
      },
    };
  return {
    ...REDISTRIBUTION_PRICING_POLICY_DEFAULT_VALUES,
  };
};

function getRefinementDefaults(
  section: IRedistributionPricingSectionPayload,
  supplierTypeIds: Array<number>,
  supplierTypesLookup: Record<string, ISupplierTypeOptionItem>,
): Pick<IRedistributionPolicyItem, 'refinementFlags' | 'definition'> {
  const definition: IRedistributionPolicyItem['definition'] = {
    supplierTypes: {},
  };
  const refinementFlags: IRedistributionPolicyItem['refinementFlags'] = {
    diameter: false,
    season: false,
    supplierType: false,
    tyreType: false,
  };
  const uniqVehicleTypes = uniq(
    section.policies.map((item) => item.tyreType?.key).filter(Boolean),
  ) as Array<string>;
  const uniqSeasons = uniq(
    section.policies
      .map((item) => item.season?.name)
      .filter(Boolean) as Array<string>,
  );
  const groupedPolicyByRefinementKey = section.policies.reduce(
    (acc, policy) => {
      if (policy.season !== null) {
        refinementFlags.season = true;
      }
      if (policy.diameter !== null) {
        refinementFlags.diameter = true;
      }
      if (policy.tyreType !== null) {
        refinementFlags.tyreType = true;
      }
      if (policy.supplierType !== null) {
        refinementFlags.supplierType = true;
      }

      const diameterKey = policy.diameter
        ? `${policy.diameter.from}-${policy.diameter.operator}-${policy.diameter.to}`
        : NO_REFINEMENT_VALUE;
      const seasonKey = policy.season
        ? policy.season.name
        : NO_REFINEMENT_VALUE;
      const tyreTypeKey = policy.tyreType
        ? policy.tyreType.key
        : NO_REFINEMENT_VALUE;
      const supplierTypeKey = policy.supplierType
        ? policy.supplierType.key
        : NO_REFINEMENT_VALUE;
      const refinementKeys = [
        supplierTypeKey,
        diameterKey,
        seasonKey,
        tyreTypeKey,
      ];
      set(acc, refinementKeys, policy);
      return acc;
    },
    {} as Record<
      string,
      Record<
        string,
        Record<string, Record<string, (typeof section.policies)[0]>>
      >
    >,
  );
  const vehicleTypeKeys = [...uniqVehicleTypes, NO_REFINEMENT_VALUE];
  const seasonKeys = [NO_REFINEMENT_VALUE, ...uniqSeasons];
  const supplierTypeKeys = [...supplierTypeIds, NO_REFINEMENT_VALUE];
  supplierTypeKeys.forEach((supplierTypeId) => {
    const policiesBySupplierType = groupedPolicyByRefinementKey[supplierTypeId];
    if (!policiesBySupplierType) {
      return;
    }
    const supplierTypeCodename =
      supplierTypeId === NO_REFINEMENT_VALUE
        ? NO_REFINEMENT_VALUE
        : supplierTypesLookup[supplierTypeId].value;
    const diameters = Object.keys(policiesBySupplierType);
    definition['supplierTypes'][supplierTypeCodename] = { refinements: [] };
    diameters.forEach((diameterGroupKey, diameterIndex) => {
      const diameter =
        diameterGroupKey === NO_REFINEMENT_VALUE
          ? null
          : ({
              from: Number(diameterGroupKey.split('-')[0]),
              operator: diameterGroupKey.split('-')[1],
              to: Number(diameterGroupKey.split('-')[2]),
            } as IDiameter);
      definition['supplierTypes'][supplierTypeCodename].refinements.push({
        diameter: diameter,
        seasons: {},
      });
      seasonKeys.forEach((seasonKey) => {
        definition['supplierTypes'][supplierTypeCodename].refinements[
          diameterIndex
        ].seasons[seasonKey] = { tyreTypes: {} };
        vehicleTypeKeys.forEach((vehicleTypeKey) => {
          const value = get(groupedPolicyByRefinementKey, [
            supplierTypeId,
            diameterGroupKey,
            seasonKey,
            vehicleTypeKey,
          ])?.priceValue.c;
          definition['supplierTypes'][supplierTypeCodename].refinements[
            diameterIndex
          ].seasons[seasonKey]['tyreTypes'][vehicleTypeKey] = { value };
        });
      });
    });
  });
  return { definition, refinementFlags };
}
